import { useCallback, useEffect, useState } from "react";
import { parseApiError } from "utils/errors/parseApiError";

/**
 * Shared pieces of the email-OTP gate. Card issuance and merchant signup both run it, and the
 * subtle parts — which failures may be retried, how many digits, when resend unlocks — must not
 * drift apart between them: a wrong guess costs one of five server-side attempts, so a
 * feature-local copy of this logic is a feature-local way to burn them.
 */

/** Matches the backend's generateOtp. */
export const OTP_LENGTH = 5;

/** The server's own floor between sends is 60s; 120s keeps a normal resend clear of it. */
export const RESEND_COUNTDOWN_SECONDS = 120;

/**
 * Failures whose only recovery is a NEW code, never another guess.
 *
 * EMAIL_NOT_VERIFIED is in here because it comes from the SIGNUP call, not /verifyOtp: the address
 * was verified but its 30-minute window lapsed, or a concurrent signup already spent it. A client
 * that branches only on the OTP codes misses it and offers a retry that can never succeed.
 */
const NEEDS_NEW_CODE = new Set(["OTP_EXPIRED", "RATE_LIMITED", "EMAIL_NOT_VERIFIED"]);

/** Digits only, capped — so the field can never hold a value the server rejects on shape. */
export const sanitizeOtp = (raw: string): string =>
  raw.replace(/\D/g, "").slice(0, OTP_LENGTH);

export interface OtpFailure {
  code: string;
  /** Display-ready text, already resolved by parseApiError. */
  message: string;
  /** Wrong digits. The guess is spent; clear the field so a retry cannot spend another. */
  isWrongCode: boolean;
  /** Dead code or guess cap — the operator needs a fresh one, not another attempt. */
  needsNewCode: boolean;
}

export function classifyOtpFailure(err: unknown, fallback: string): OtpFailure {
  const { code, message } = parseApiError(err, fallback);
  return {
    code,
    message,
    isWrongCode: code === "OTP_INVALID",
    needsNewCode: NEEDS_NEW_CODE.has(code),
  };
}

/**
 * Codes from the SIGNUP leg that no retry can fix — the operator must change something (usually
 * the email, via Back) or the record already exists.
 */
const SIGNUP_TERMINAL = new Set([
  "USER_EXIST",
  "EMAIL_IN_USE",
  "CARD_ALREADY_REGISTERED",
  "MERCHANT_ALREADY_SIGNED_UP",
  "ALREADY_EXISTS",
  "WEAK_PASSWORD",
  "SERVICE_MISCONFIGURED",
]);

/** Preflight codes no retry can fix — each is a fact about the card or the cardholder. */
const PREFLIGHT_TERMINAL = new Set([
  "CARD_ALREADY_REGISTERED",
  // The merchant preflight's refusals: the id is taken, or taken by someone else.
  "MERCHANT_ALREADY_SIGNED_UP",
  "MERCHANT_NOT_FOUND",
  "CARD_NOT_FOUND",
  "CARD_REVOKED",
  "CARD_EXPIRED",
  "CARD_SUSPENDED",
  "CARD_NOT_ACTIVE",
  "USER_EXIST",
  "EMAIL_IN_USE",
  "INVALID_INPUT",
]);

export interface PreflightFailure {
  code: string;
  message: string;
  /** Retrying cannot help; offering "try again" just loops. */
  isTerminal: boolean;
}

/**
 * Kept apart from classifySignupFailure despite the overlap: this leg has sent no email, so none
 * of its failures can mean "the code is dead".
 */
export function classifyPreflightFailure(
  err: unknown,
  fallback: string,
): PreflightFailure {
  const { code, message } = parseApiError(err, fallback);
  return {
    code,
    message,
    isTerminal: PREFLIGHT_TERMINAL.has(code),
  };
}

export interface SignupFailure {
  code: string;
  message: string;
  /** The verification lapsed or was already spent — a fresh code is required. */
  needsNewCode: boolean;
  /** Retrying cannot help; offering "try again" just loops. */
  isTerminal: boolean;
}

/**
 * Classify a failure from the SIGNUP call, which runs immediately after /verifyOtp on the same
 * click. It must NOT go through classifyOtpFailure.
 *
 * The trap: RATE_LIMITED on this leg is Firebase Auth throttling
 * (FIREBASE_AUTH_CODES.TOO_MANY_ATTEMPTS -> GENERIC.RATE_LIMITED), NOT the OTP guess cap. Reading
 * it as a dead code discards a verification that is still perfectly valid and sends the operator
 * to request a code they never needed — while hiding the real, transient cause.
 */
export function classifySignupFailure(
  err: unknown,
  fallback: string,
): SignupFailure {
  const { code, message } = parseApiError(err, fallback);
  return {
    code,
    message,
    needsNewCode: code === "EMAIL_NOT_VERIFIED",
    isTerminal: SIGNUP_TERMINAL.has(code),
  };
}

/**
 * Countdown to the next allowed resend.
 *
 * Deadline-based rather than decrement-based: a `setSecondsLeft(s - 1)` interval that is torn down
 * and rebuilt each tick drifts past the real cooldown, and stalls entirely while a background tab
 * is throttled. Comparing against a fixed deadline is immune to both.
 */
export function useResendCountdown(): {
  secondsLeft: number;
  start: () => void;
  stop: () => void;
} {
  const [deadline, setDeadline] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!deadline) {
      setSecondsLeft(0);
      return;
    }
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [deadline]);

  // Stable identities: an unstable start/stop leaks into every consumer's dependency arrays.
  const start = useCallback(
    () => setDeadline(Date.now() + RESEND_COUNTDOWN_SECONDS * 1000),
    [],
  );
  const stop = useCallback(() => setDeadline(0), []);

  return { secondsLeft, start, stop };
}

/** mm:ss for the "you can request a new code in …" line. */
export function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
